<template>
  <div class="device_control_page_wraper rgbw_editor">
    <div class="close_img_top">
      <img
        src="~assets/img/controlpublic/control-guanbi-chahao.png"
        @click="$closeControlToast"
        alt="关闭"
      />
    </div>
    <div class="devce_name z_ellipsis1" :title="objData.remark">{{ objData.remark }}</div>
    <section class="rgbw-status">
      <span :style="{ background: statusLeadingColor }" class="rgbw-status-color">
        {{ controlData.openStateValue3 > 0 ? '≈' : '' }}
      </span>
      <div>
        <strong>{{ statusTitle }}</strong>
        <small>{{ statusDetail }}</small>
      </div>
    </section>
    <div class="rgbw_presets">
      <button
        v-for="preset in colorPresets"
        :key="preset.name"
        :title="preset.name"
        :style="{ background: preset.color }"
        @click="applyColorPreset(preset)"
      ></button>
    </div>
    <nav class="rgbw-tabs" aria-label="灯光调节方式">
      <button
        v-for="section in sections"
        :key="section.key"
        :class="{ active: activeSection === section.key }"
        @click="activeSection = section.key"
      >
        {{ section.label }}
      </button>
    </nav>
    <div class="rgbw-panels">
      <section v-show="activeSection === 'wheel'" class="rgbw-wheel-panel">
        <div
          ref="colorWheel"
          class="rgbw_color_wheel"
          @pointerdown.prevent="wheelStart"
          @pointermove.prevent="wheelMove"
          @pointerup.prevent="wheelEnd"
          @pointercancel="wheelEnd"
        >
          <span class="rgbw_wheel_cursor" :style="wheelCursorStyle"></span>
        </div>
        <small>{{ whiteEnabled ? '色盘调整彩光，白光在亮度页独立设置' : '色盘调整彩光' }}</small>
      </section>
      <section v-show="activeSection === 'precise'" class="rgbw-precise-panel">
        <control-level-slider
          v-for="(key, index) in preciseKeys.slice(0, 3)"
          :key="key"
          :label="preciseLabels[index]"
          :value="controlData[key]"
          :color="channelColors[index]"
          @input="updatePreciseValue(key, $event)"
          @change="commitStatic"
          @drag="dragging = $event"
        />
      </section>
      <section v-show="activeSection === 'brightness'" class="rgbw-brightness-panel">
        <control-level-slider
          label="彩光"
          :max="100"
          :value="picker.v"
          :color="valueColor"
          @input="previewColorBrightness"
          @change="commitStatic"
          @drag="dragging = $event"
        />
        <control-level-slider
          v-if="whiteEnabled"
          label="白光"
          :max="100"
          :value="percent(controlData.openStateValue2)"
          color="#d5d2c8"
          @input="previewWhitePercent"
          @change="commitStatic"
          @drag="dragging = $event"
        />
      </section>
      <section v-show="activeSection === 'mode'" class="rgbw-mode-panel">
        <button
          v-for="mode in modes"
          :key="mode.value"
          :class="{ active: controlData.openStateValue3 === mode.value }"
          @click="selectMode(mode.value)"
        >
          {{ mode.label }}
        </button>
      </section>
    </div>
    <button
      class="device_control_btn public_control_btn rgbw_power"
      :class="{ public_control_btn_on: isOn }"
      @click="togglePower"
    >
      {{ isOn ? '已打开' : '已关闭' }}
    </button>
  </div>
</template>
<script>
import ControlLevelSlider from '@/components/common/control-level-slider.vue'
import { request, ReturnBaseUrl } from '@/network/request'
import store from '@/store'
import { getLightColorName } from '@/common/rgb-light-color'
const controlKeys = [
  'openState',
  'openStateValue',
  'openStateValue1',
  'openStateValue2',
  'openStateValue3'
]
const copyState = value =>
  controlKeys.reduce((result, key) => {
    result[key] = Number(value[key]) || 0
    return result
  }, {})

const clampByte = value => Math.max(0, Math.min(255, Math.round(Number(value) || 0)))
// Same display-only blue correction as the app; device channel bytes are unchanged.
const rgbDisplayColor = (red, green, blue) => {
  const rgb = [red, green, blue].map(clampByte)
  return rgb[2] > 210 && rgb[2] >= rgb[0] * 1.5 && rgb[2] >= rgb[1] * 1.5
    ? '#0a84ff'
    : 'rgb(' + rgb.join(',') + ')'
}
const byteHex = value =>
  clampByte(value)
    .toString(16)
    .padStart(2, '0')
const hsvToRgb = ({ h, s, v }) => {
  const saturation = s / 100
  const brightness = v / 100
  const chroma = brightness * saturation
  const section = (((h % 360) + 360) % 360) / 60
  const x = chroma * (1 - Math.abs((section % 2) - 1))
  const values =
    section < 1
      ? [chroma, x, 0]
      : section < 2
      ? [x, chroma, 0]
      : section < 3
      ? [0, chroma, x]
      : section < 4
      ? [0, x, chroma]
      : section < 5
      ? [x, 0, chroma]
      : [chroma, 0, x]
  const match = brightness - chroma
  return values.map(value => clampByte((value + match) * 255))
}
const rgbToHsv = (red, green, blue) => {
  const values = [red, green, blue].map(value => clampByte(value) / 255)
  const max = Math.max(...values)
  const min = Math.min(...values)
  const delta = max - min
  let hue = 0
  if (delta && max === values[0]) hue = 60 * (((values[1] - values[2]) / delta) % 6)
  else if (delta && max === values[1]) hue = 60 * ((values[2] - values[0]) / delta + 2)
  else if (delta) hue = 60 * ((values[0] - values[1]) / delta + 4)
  return {
    h: Math.round((hue + 360) % 360),
    s: Math.round(max ? (delta / max) * 100 : 0),
    v: Math.round(max * 100)
  }
}

export default {
  components: { ControlLevelSlider },
  props: {
    objData: { type: Object, required: true },
    realtime: { type: Boolean, default: false },
    whiteEnabled: { type: Boolean, default: true }
  },
  data() {
    const source = this.realtime ? { ...this.objData } : this.objData
    source.openState = clampByte(source.openState)
    source.openStateValue = clampByte(source.openStateValue)
    source.openStateValue1 = clampByte(source.openStateValue1)
    // 030 RGB与029共用BC字段布局，但W字段必须始终保持为0。
    source.openStateValue2 = this.whiteEnabled ? clampByte(source.openStateValue2) : 0
    source.openStateValue3 = Math.max(0, Math.min(7, Number(source.openStateValue3) || 0))
    const picker = rgbToHsv(source.openState, source.openStateValue, source.openStateValue1)
    const maximum = Math.max(source.openState, source.openStateValue, source.openStateValue1)
    return {
      controlData: source,
      activeSection:
        source.openStateValue3 > 0
          ? 'mode'
          : source.openState + source.openStateValue + source.openStateValue1 === 0 &&
            source.openStateValue2 > 0
          ? 'brightness'
          : 'wheel',
      sections: [
        { key: 'wheel', label: '色盘调色' },
        { key: 'precise', label: '精细调色' },
        { key: 'brightness', label: '亮度调整' },
        { key: 'mode', label: '灯光模式' }
      ],
      controlSessionToken: store.state.token,
      controlServerAddress: ReturnBaseUrl(),
      requestInFlight: false,
      pendingControl: null,
      localRevision: 0,
      acknowledgedState: copyState(source),
      dragging: false,

      picker,
      lastColorBrightness: picker.v > 0 ? picker.v : 100,
      colorBasis:
        maximum > 0
          ? [
              source.openState / maximum,
              source.openStateValue / maximum,
              source.openStateValue1 / maximum
            ]
          : [1, 1, 1],
      channelColors: ['#ff0000', '#00ff00', '#0a84ff', '#d5d2c8'],
      preciseLabels: ['R', 'G', 'B'].concat(this.whiteEnabled ? ['W'] : []),
      preciseKeys: ['openState', 'openStateValue', 'openStateValue1'].concat(
        this.whiteEnabled ? ['openStateValue2'] : []
      ),
      lastStatic: null,
      lastMode: source.openStateValue3,
      channels: [
        { key: 'openState', shortName: 'R', name: '红光', dotClass: 'red' },
        { key: 'openStateValue', shortName: 'G', name: '绿光', dotClass: 'green' },
        { key: 'openStateValue1', shortName: 'B', name: '蓝光', dotClass: 'blue' }
      ].concat(
        this.whiteEnabled
          ? [{ key: 'openStateValue2', shortName: 'W', name: '白光', dotClass: 'white' }]
          : []
      ),
      colorPresets: [
        { name: '红', color: '#ff0000', rgb: [255, 0, 0] },
        { name: '绿', color: '#00ff00', rgb: [0, 255, 0] },
        { name: '蓝', color: '#0a84ff', rgb: [0, 0, 255] },
        { name: '黄', color: '#ffff00', rgb: [255, 255, 0] },
        { name: '青', color: '#00ffff', rgb: [0, 255, 255] },
        { name: '紫', color: '#ff00ff', rgb: [255, 0, 255] }
      ],
      modes: [
        { value: 0, label: '静态调光' },
        { value: 5, label: 'RGB 呼吸' },
        { value: 1, label: '红光呼吸' },
        { value: 2, label: '绿光呼吸' },
        { value: 3, label: '蓝光呼吸' },
        { value: 4, label: '白光呼吸' },
        { value: 6, label: '黄光呼吸' },
        { value: 7, label: '紫光呼吸' }
      ]
    }
  },
  computed: {
    statusTitle() {
      return !this.isOn
        ? '已关闭'
        : this.controlData.openStateValue3 > 0
        ? this.currentModeLabel
        : '静态调光'
    },
    statusDetail() {
      if (!this.isOn) return '所有灯光通道均为 0'
      if (this.controlData.openStateValue3 > 0) return '动态灯光模式'
      const c = this.controlData
      return (
        this.preciseKeys.map((key, index) => this.preciseLabels[index] + ' ' + c[key]).join(' ') +
        ' · ' +
        getLightColorName(
          c.openState,
          c.openStateValue,
          c.openStateValue1,
          this.whiteEnabled ? c.openStateValue2 : 0
        )
      )
    },
    previewColor() {
      const c = this.controlData,
        w = this.whiteEnabled ? c.openStateValue2 : 0
      const rgb = [
        c.openState + w,
        c.openStateValue + Math.round((w * 242) / 255),
        c.openStateValue1 + Math.round((w * 199) / 255)
      ].map(clampByte)
      return rgb.some(value => value > 0) ? rgbDisplayColor(...rgb) : '#50545d'
    },
    statusLeadingColor() {
      if (!this.isOn) return '#5b5f67'
      const mode = this.controlData.openStateValue3
      if (!mode) return this.previewColor
      const colors = {
        1: '#ff0000',
        2: '#00ff00',
        3: '#0a84ff',
        4: '#f2f2f2',
        6: '#ffff00',
        7: '#ff00ff'
      }
      return (
        colors[mode] ||
        (this.controlData.openState +
          this.controlData.openStateValue +
          this.controlData.openStateValue1 >
        0
          ? this.previewColor
          : '#6e7ee8')
      )
    },
    isOn() {
      return (
        this.controlData.openState > 0 ||
        this.controlData.openStateValue > 0 ||
        this.controlData.openStateValue1 > 0 ||
        (this.whiteEnabled && this.controlData.openStateValue2 > 0) ||
        this.controlData.openStateValue3 > 0
      )
    },
    draftRgb() {
      return hsvToRgb(this.picker)
    },
    draftColor() {
      return `#${this.draftRgb.map(byteHex).join('')}`
    },
    valueColor() {
      const color = hsvToRgb({ h: this.picker.h, s: this.picker.s, v: 100 })
      return `rgb(${color.join(',')})`
    },
    wheelCursorStyle() {
      const radians = (this.picker.h * Math.PI) / 180
      const radius = this.picker.s * 0.46
      return {
        left: `${50 + Math.sin(radians) * radius}%`,
        top: `${50 - Math.cos(radians) * radius}%`,
        background: this.draftColor
      }
    },
    currentModeLabel() {
      const mode = this.modes.find(item => item.value === this.controlData.openStateValue3)
      return mode ? mode.label : '静态调光'
    }
  },
  watch: {
    objData: {
      deep: true,
      handler(newValue) {
        if (!this.realtime || !newValue) return
        this.syncRealtimeState(newValue)
      }
    }
  },
  methods: {
    syncRealtimeState(source) {
      if (this.requestInFlight || this.pendingControl || this.dragging) return
      this.acknowledgedState = copyState(source)
      const values = [
        clampByte(source.openState),
        clampByte(source.openStateValue),
        clampByte(source.openStateValue1),
        this.whiteEnabled ? clampByte(source.openStateValue2) : 0
      ]
      const mode = Math.max(0, Math.min(7, Number(source.openStateValue3) || 0))
      const matchesCurrentState =
        values[0] === Number(this.controlData.openState) &&
        values[1] === Number(this.controlData.openStateValue) &&
        values[2] === Number(this.controlData.openStateValue1) &&
        values[3] === Number(this.controlData.openStateValue2) &&
        mode === Number(this.controlData.openStateValue3)

      // 自身成功回写只确认状态，不能让低亮度取整值覆盖原始 RGB 比例基准。
      if (matchesCurrentState) return

      this.controlData.openState = values[0]
      this.controlData.openStateValue = values[1]
      this.controlData.openStateValue1 = values[2]
      this.controlData.openStateValue2 = values[3]
      this.controlData.openStateValue3 = mode
      this.rememberColor()
      if (values.some(value => value > 0)) this.lastStatic = values.slice()
    },
    previewColorBrightness(value) {
      this.picker.v = value
      this.applyColorBrightness()
    },
    previewWhitePercent(value) {
      this.controlData.openStateValue2 = clampByte((value * 255) / 100)
      this.previewWhiteBrightness()
    },
    previewWhiteBrightness() {
      this.localRevision += 1
      this.controlData.openStateValue3 = 0
    },
    percent(value) {
      return Math.round((clampByte(value) * 100) / 255)
    },
    submit() {
      if (!this.whiteEnabled) this.controlData.openStateValue2 = 0
      if (!this.realtime) return
      this.pendingControl = { revision: ++this.localRevision, state: copyState(this.controlData) }
      this.flushControlRequest()
    },
    isCurrentControlSession() {
      return this.controlSessionToken === store.state.token && this.controlServerAddress === ReturnBaseUrl()
    },
    flushControlRequest() {
      // A previous login's delayed response must not send commands with the new login's token.
      if (!this.isCurrentControlSession()) {
        this.pendingControl = null
        return
      }
      if (this.requestInFlight || !this.pendingControl) return
      const current = this.pendingControl
      this.pendingControl = null
      this.requestInFlight = true
      request({
        suppressErrorToast: true,
        method: 'post',
        url: '/device/updateDeiviceControl',
        data: Object.assign(
          { token: store.state.token, deviceId: this.objData.deviceId, password: '' },
          current.state
        )
      })
        .then(res => {
          if (!this.isCurrentControlSession()) return
          if (!res.data || res.data.code != 0) throw res.data || new Error('控制失败')
          this.acknowledgedState = copyState(current.state)
          if (!this.pendingControl && current.revision === this.localRevision)
            this.applyAcknowledgedState(current.state)
        })
        .catch(error => {
          if (!this.isCurrentControlSession()) return
          if (!this.pendingControl && current.revision === this.localRevision) {
            Object.assign(this.controlData, this.acknowledgedState)
            this.rememberColor()
            this.applyAcknowledgedState(this.acknowledgedState)
          }
          this.$showTipsToast(3, (error && error.message) || '控制失败，请重试', true, 3000)
        })
        .finally(() => {
          this.requestInFlight = false
          this.flushControlRequest()
        })
    },
    applyAcknowledgedState(state) {
      controlKeys.forEach(key => this.$set(this.objData, key, state[key]))
      ;[store.state.publicinfo.homeServeData, store.state.publicinfo.regionServeData].forEach(
        data => {
          if (!data || !Array.isArray(data.deviceList)) return
          const item = data.deviceList.find(
            item => String(item.deviceId) === String(this.objData.deviceId)
          )
          if (item) controlKeys.forEach(key => this.$set(item, key, state[key]))
        }
      )
    },
    wheelStart(event) {
      if (this.picker.v <= 0) this.picker.v = this.lastColorBrightness
      this.dragging = true
      event.currentTarget.setPointerCapture(event.pointerId)
      this.selectWheel(event)
      this.applyPickerValues()
    },
    wheelMove(event) {
      if (!this.dragging) return
      this.selectWheel(event)
      this.applyPickerValues()
    },
    wheelEnd(event) {
      if (!this.dragging) return
      this.dragging = false
      this.commitStatic()
    },
    startSliderDrag(event) {
      this.dragging = true
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    endSliderDrag() {
      this.dragging = false
    },
    commitStatic() {
      this.dragging = false
      this.channels.forEach(channel => {
        this.controlData[channel.key] = clampByte(this.controlData[channel.key])
      })
      this.controlData.openStateValue3 = 0
      this.submit()
    },
    updatePreciseValue(key, value) {
      this.localRevision += 1
      this.controlData[key] = clampByte(value)
      this.controlData.openStateValue3 = 0
      if (key !== 'openStateValue2') this.rememberColor()
    },
    rememberColor() {
      const red = clampByte(this.controlData.openState)
      const green = clampByte(this.controlData.openStateValue)
      const blue = clampByte(this.controlData.openStateValue1)
      const maximum = Math.max(red, green, blue)
      if (maximum <= 0) {
        // App derives brightness from current channels; retain only the previous color basis.
        this.picker.v = 0
        return
      }
      this.picker = rgbToHsv(red, green, blue)
      this.lastColorBrightness = this.picker.v
      this.colorBasis = [red / maximum, green / maximum, blue / maximum]
    },
    applyColorBrightness() {
      this.localRevision += 1
      const brightness = Math.round(
        (Math.max(0, Math.min(100, Number(this.picker.v) || 0)) * 255) / 100
      )
      const basis = this.colorBasis || [1, 1, 1]
      this.controlData.openState = Math.round(basis[0] * brightness)
      this.controlData.openStateValue = Math.round(basis[1] * brightness)
      this.controlData.openStateValue1 = Math.round(basis[2] * brightness)
      if (this.picker.v > 0) this.lastColorBrightness = this.picker.v
      this.controlData.openStateValue3 = 0
    },
    selectWheel(event) {
      this.localRevision += 1
      const point = event.touches ? event.touches[0] : event
      const rect = this.$refs.colorWheel.getBoundingClientRect()
      const radius = rect.width / 2
      const x = point.clientX - rect.left - radius
      const y = point.clientY - rect.top - radius
      const distance = Math.min(Math.sqrt(x * x + y * y), radius)
      this.picker.h = Math.round(((Math.atan2(y, x) * 180) / Math.PI + 450) % 360)
      this.picker.s = Math.round((distance / radius) * 100)
      this.applyPickerValues()
    },
    selectWheelOnce(event) {
      if (this.picker.v <= 0) this.picker.v = this.lastColorBrightness
      this.selectWheel(event)
      this.commitPickerColor()
    },
    applyPickerValues() {
      ;[
        this.controlData.openState,
        this.controlData.openStateValue,
        this.controlData.openStateValue1
      ] = this.draftRgb
      if (this.picker.v > 0) this.lastColorBrightness = this.picker.v
      this.controlData.openStateValue3 = 0
      const maximum = Math.max(
        this.controlData.openState,
        this.controlData.openStateValue,
        this.controlData.openStateValue1
      )
      if (maximum > 0) {
        this.colorBasis = [
          this.controlData.openState / maximum,
          this.controlData.openStateValue / maximum,
          this.controlData.openStateValue1 / maximum
        ]
      }
    },
    commitPickerColor() {
      this.applyPickerValues()
      this.commitStatic()
    },
    applyColorPreset(preset) {
      const c = this.controlData
      const brightness =
        Math.max(c.openState, c.openStateValue, c.openStateValue1) ||
        Math.round((this.lastColorBrightness * 255) / 100) ||
        255
      ;[c.openState, c.openStateValue, c.openStateValue1] = preset.rgb.map(value =>
        Math.round((value * brightness) / 255)
      )
      this.rememberColor()
      this.commitStatic()
    },
    selectMode(value) {
      if (!this.whiteEnabled) this.controlData.openStateValue2 = 0
      this.controlData.openStateValue3 = Math.max(0, Math.min(7, Number(value) || 0))
      this.lastMode = this.controlData.openStateValue3
      this.submit()
    },
    togglePower() {
      const keys = this.channels.map(channel => channel.key)
      if (this.isOn) {
        this.lastMode = this.controlData.openStateValue3
        this.lastStatic = keys.map(key => this.controlData[key])
        if (this.picker.v > 0) this.lastColorBrightness = this.picker.v
        keys.forEach(key => {
          this.controlData[key] = 0
        })
        this.controlData.openStateValue3 = 0
        this.picker.v = 0
      } else {
        this.controlData.openStateValue3 = this.lastMode
        const values = this.lastStatic || (this.whiteEnabled ? [255, 255, 255, 0] : [255, 255, 255])
        keys.forEach((key, index) => {
          this.controlData[key] = values[index]
        })
        this.picker = rgbToHsv(values[0], values[1], values[2])
        if (this.picker.v > 0) this.lastColorBrightness = this.picker.v
        this.rememberColor()
      }
      this.submit()
    }
  }
}
</script>
<style scoped>
.rgbw_editor {
  width: 340px;
  max-width: calc(100vw - 68px);
  height: auto;
  box-sizing: border-box;
  align-items: stretch;
  color: #d9dce2;
  cursor: default;
}
.rgbw_editor > * {
  flex-shrink: 0;
}
.rgbw_editor > .close_img_top {
  height: 22px;
}
.rgbw_editor > .close_img_top img {
  width: 21px;
  height: 21px;
  cursor: pointer;
}
.rgbw_editor > .devce_name {
  width: 100%;
  height: 32px;
  line-height: 32px;
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 400;
  text-align: center;
}
.rgbw-status {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px;
  border-radius: 10px;
  background: #ffffff02;
  margin: 0 0 10px;
  min-width: 0;
}
.rgbw-status > div {
  min-width: 0;
  flex: 1;
}
.rgbw-status-color {
  width: 32px;
  height: 30px;
  border-radius: 8px;
  flex: none;
  display: grid;
  place-items: center;
  font-size: 22px;
}
.rgbw-status strong {
  display: block;
  font-size: 13px;
  font-weight: 400;
  line-height: 22px;
}
.rgbw-status small {
  display: block;
  font-size: 10px;
  color: #8e939c;
  white-space: nowrap;
  line-height: 18px;
}
.rgbw_presets {
  display: flex;
  justify-content: space-around;
  margin: 0 0 10px;
}
.rgbw_presets button {
  width: 25px;
  height: 25px;
  padding: 0;
  border: 1px solid #d9dce2;
  border-radius: 50%;
  cursor: pointer;
}
.rgbw-tabs {
  display: flex;
  padding: 3px;
  border-radius: 6px;
  background: #1f242c;
  gap: 2px;
}
.rgbw-tabs button {
  flex: 1;
  min-width: 0;
  border: 0;
  background: none;
  color: #b6bac2;
  font: inherit;
  font-size: 12px;
  padding: 11px 1px;
  border-radius: 5px;
  white-space: nowrap;
  cursor: pointer;
}
.rgbw-tabs button.active {
  background: #48515f;
  color: #fff;
}
.rgbw-panels {
  height: 210px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rgbw-wheel-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.rgbw-wheel-panel small {
  font-size: 11px;
  color: #969ca8;
}
.rgbw_color_wheel {
  position: relative;
  width: 176px;
  height: 176px;
  border-radius: 50%;
  background: radial-gradient(circle, #fff 0%, #ffffff00 70%),
    conic-gradient(#ff0000, #ffff00, #00ff00, #00ffff, #0a84ff, #ff00ff, #ff0000);
  cursor: pointer;
  touch-action: none;
}
.rgbw_wheel_cursor {
  position: absolute;
  width: 17px;
  height: 17px;
  border: 2px solid white;
  border-radius: 50%;
  box-sizing: border-box;
  transform: translate(-50%, -50%);
  box-shadow: 0 1px 5px #0008;
  pointer-events: none;
}
.rgbw-precise-panel,
.rgbw-brightness-panel {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}
.rgbw-mode-panel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  width: 100%;
}
.rgbw-mode-panel button {
  padding: 12px 2px;
  border: 1px solid #8c929d;
  border-radius: 6px;
  background: #1f242c;
  color: #c9cdd5;
  font-size: 11px;
  cursor: pointer;
}
.rgbw-mode-panel button.active {
  background: #3f4d69;
  color: #fff;
}
.rgbw_power {
  display: block;
  flex: none;
  width: 100%;
  height: 44px;
  line-height: 30px;
  box-sizing: border-box;
  margin: 5px 0 22px;
  border: 1px solid #8c929d;
  border-radius: 5px;
  color: #fff;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
}
.rgbw_power.public_control_btn_on {
  background: #fff;
  color: #282828;
}
</style>
